import React from 'react';
import Footer from '../components/Footer';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 text-white py-20 px-4 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
          <div className="absolute top-32 right-20 w-16 h-16 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-32 right-1/3 w-24 h-24 border-2 border-white rounded-full"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-block bg-white bg-opacity-25 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border border-white border-opacity-30">
              <span className="text-sm font-medium text-white">🌟 Every Student A Star</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight text-white">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">PAK School</span>
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed font-light">
            Nurturing excellence, fostering character, and empowering every student to shine like a star
          </p>
          <div className="mt-12 flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Our Foundation
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The core principles that guide our educational philosophy and shape our students' character
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[
              { value: 'Honesty', icon: '🤝', color: 'from-blue-500 to-blue-600' },
              { value: 'Hard work', icon: '💪', color: 'from-green-500 to-green-600' },
              { value: 'Respect for others', icon: '🙏', color: 'from-purple-500 to-purple-600' },
              { value: 'Cleanliness', icon: '✨', color: 'from-pink-500 to-pink-600' },
              { value: 'Unconditional love and kindness', icon: '❤️', color: 'from-red-500 to-red-600' },
              { value: 'Justice', icon: '⚖️', color: 'from-indigo-500 to-indigo-600' },
              { value: 'Forgiveness', icon: '🕊️', color: 'from-teal-500 to-teal-600' },
              { value: 'Thankfulness', icon: '🙏', color: 'from-orange-500 to-orange-600' },
              { value: 'Compassion', icon: '💝', color: 'from-rose-500 to-rose-600' },
              { value: 'Tolerance', icon: '🌍', color: 'from-cyan-500 to-cyan-600' },
              { value: 'Cooperation', icon: '🤝', color: 'from-emerald-500 to-emerald-600' }
            ].map((item, index) => (
              <div key={index} className="group">
                <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-2xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                    {item.icon}
                  </div>
                  <h3 className="text-center font-semibold text-gray-800 text-sm leading-tight">
                    {item.value}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-white relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-green-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Our Purpose
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Mission</h2>
          </div>
          
          <div className="relative">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 md:p-12 rounded-3xl shadow-xl border border-green-100">
              <div className="absolute top-6 right-6 w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl">🎯</span>
              </div>
              <div className="pr-20">
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium">
                  To give quality education with major emphasis on character building, applied knowledge and skills, 
                  enabling the students to successfully enter the professional colleges, with sound educational background.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white relative overflow-hidden">
        {/* Stars Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 text-yellow-300 text-2xl animate-pulse">⭐</div>
          <div className="absolute top-40 right-20 text-yellow-300 text-xl animate-pulse delay-1000">✨</div>
          <div className="absolute bottom-32 left-1/4 text-yellow-300 text-3xl animate-pulse delay-500">🌟</div>
          <div className="absolute bottom-20 right-1/3 text-yellow-300 text-xl animate-pulse delay-700">⭐</div>
          <div className="absolute top-1/2 left-1/2 text-yellow-300 text-2xl animate-pulse delay-300">✨</div>
        </div>
        
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm px-8 py-4 rounded-full mb-6">
              <span className="text-lg font-medium">🌟 Vision Statement</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">
              EVERY STUDENT A STAR
            </span>
          </h2>
          
          <div className="bg-white bg-opacity-15 backdrop-blur-md p-8 md:p-12 rounded-3xl border border-white border-opacity-30">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl md:text-2xl leading-relaxed mb-8 font-light text-white">
                At our school, our vision is to empower every student to shine brilliantly like a star, 
                fostering their unique talents and potential.
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-pink-400 mx-auto mb-8"></div>
              <p className="text-lg md:text-xl leading-relaxed text-white">
                PAK School cultivates well-rounded individuals in an environment that ignites curiosity, 
                hones talent, and shapes character. With dedicated faculty, modern facilities, and a balanced 
                curriculum that values tradition and innovation, we offer a transformative educational experience. 
                Join us in celebrating every achievement and empowering students to dream big, think critically, 
                and embrace lifelong learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CEO Message */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Message from the CEO
            </h2>
            <p className="text-lg text-gray-600">PAK School & College System Mardan</p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-4"></div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 md:p-8 rounded-lg shadow-lg">
            <div className="flex flex-col md:flex-row items-center mb-6">
              <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 md:mb-0 md:mr-6">
                W
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Mrs. Wasim</h3>
                <p className="text-blue-600 font-medium">CEO, PAK School & College System Mardan</p>
              </div>
            </div>
            
            <div className="text-gray-700 leading-relaxed space-y-4">
              <p>
                As CEO of our esteemed school system, I'm thrilled to welcome you to our vibrant community 
                dedicated to nurturing the brilliance within each and every one of our students.
              </p>
              <p>
                At PAK School & College System, our mission is simple yet profound: "Every Student A Star" 
                - to ignite a passion for learning and empower every student to shine brightly in their own unique way. 
                With a commitment to excellence and innovation, we strive to cultivate an environment where curiosity 
                flourishes, diversity is celebrated and potential knows no bounds.
              </p>
              <p>
                Together, let's embark on a transformative journey where every student's journey is guided by their 
                own constellation of dreams and aspirations. With unwavering support from our dedicated educators and 
                engaged community, I am confident that together, we will inspire greatness and illuminate the path to 
                success for all.
              </p>
              <p className="font-medium text-blue-800">
                Welcome to The PAK School & College System Mardan, where each student's potential truly becomes a shining star.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Messages from Principals */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              Messages from the Heads/Principals
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Regional Head */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-3">
                  S
                </div>
                <h3 className="text-lg font-bold text-gray-800">Syed Muhammad Ilyas</h3>
                <p className="text-green-600 font-medium text-sm">Regional Head</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Welcome to PAK School & College System Mardan! Our community is dedicated to academic excellence, 
                personal growth, and the pursuit of knowledge. We prioritize student-centered learning, fostering 
                an environment that encourages 21st century skills like curiosity, critical thinking, and collaboration.
              </p>
            </div>

            {/* Vice Principal PH */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-3">
                  M
                </div>
                <h3 className="text-lg font-bold text-gray-800">Muhammad Jehanzeb</h3>
                <p className="text-purple-600 font-medium text-sm">Vice Principal</p>
              </div>
              <div className="text-center mb-3">
                <p className="text-sm italic text-gray-600 leading-relaxed">
                  "سبق پھر پڑھ صداقت کا، عدالت کا، شجاعت کا<br/>
                  لیا جائے گا تجھ سے کام دنیا کی امامت کا"
                </p>
                <p className="text-xs text-gray-500">— علامہ اقبال</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                At Pak School & College System, we are inspired by Allama Iqbal's vision—nurturing students with 
                truth, justice, and courage to lead with knowledge, faith, and integrity.
              </p>
            </div>

            {/* Principal DHA */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-pink-600 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-3">
                  S
                </div>
                <h3 className="text-lg font-bold text-gray-800">Sadia Qamar</h3>
                <p className="text-pink-600 font-medium text-sm">Principal DHA Campus</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Welcome to Pak School DHA Campus, where we are committed to nurturing young minds with excellence 
                in education. Established 27 years ago, our school provides a holistic learning environment for 
                students from Play Group to Grade 5.
              </p>
            </div>

            {/* Principal SMT */}
            <div className="bg-white p-6 rounded-lg shadow-lg md:col-span-2 lg:col-span-1">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-3">
                  M
                </div>
                <h3 className="text-lg font-bold text-gray-800">Mrs Minhaj</h3>
                <p className="text-orange-600 font-medium text-sm">Principal SMT Campus</p>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                I am honoured to lead this vibrant community of learners. Our dedicated teachers and staff are 
                committed to provide a supportive and inclusive environment that fosters academic excellence and 
                personal growth with a focus on character building.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              What makes us different?
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 md:p-8 rounded-lg shadow-lg mb-8">
            <p className="text-gray-700 leading-relaxed text-center text-base md:text-lg">
              At PAK School and College System, we take pride in what sets us apart – a distinctive blend of 
              innovation, inclusivity, and unwavering commitment to excellence. What truly distinguishes us is our 
              holistic approach to education. Beyond imparting knowledge, we emphasize the development of critical 
              thinking, creativity, and resilience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "💡",
                title: "Innovation in Education",
                description: "At the heart of our ethos lies a commitment to innovation in education."
              },
              {
                icon: "👨‍🏫",
                title: "Student-Centric Approach",
                description: "Our dedicated and experienced faculty embodies a student-centric approach."
              },
              {
                icon: "🌍",
                title: "Global Perspective",
                description: "We impart a global perspective, celebrating cultural diversity and inclusion."
              },
              {
                icon: "⭐",
                title: "Character Development",
                description: "Character development is woven into the fabric of our educational philosophy."
              },
              {
                icon: "🤝",
                title: "Community Engagement",
                description: "Beyond academics, we foster a spirit of community engagement and service."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="text-3xl mb-4 text-center">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-800 mb-3 text-center">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed text-center">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;
